/** IXD Chunk의 blockItems(키-값 목록) 렌더링 */

import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { IxdTable } from "@/components/ixd-table";
import {
  buildValueCellDataset,
  formatCellForDisplay,
} from "@/utils/ixd-editor/cell-display";
import {
  applyUnit,
  formatMonetaryTextForEditing,
  parseNumericValue,
} from "@/utils/ixd-editor/unit-conversion";
import {
  formatStoredValueForEditing,
  getEditableInputModeForSchema,
  getEditableMaxLengthForSchema,
  getIxdSchemaValueKind,
  normalizeEditedCellValue,
  sanitizeEditableInputElement,
  sanitizeEditableInputForSchema,
  skipSeparatorOnDelete,
} from "@/utils/ixd-editor/value-validation";

interface BlockItemViewProps {
  /** chunk.blockItems (라벨 → 값) */
  title: string;
  /** false이면 제목을 렌더하지 않는다. */
  showTitle?: boolean;
  content: string | number;
  sectionId: string;
  /** content가 단순 fact인 경우 table cell과 같은 schema type으로 표시 포맷을 결정한다. */
  schemaType?: string | null;
  unitText?: string | null;
  /** block item의 고유 ID (cell_id로 사용되어 포커싱 대상) */
  blockItemId?: string | null;
  /** 편집 모드 활성화 */
  editable?: boolean;
  /** 미저장 변경값 */
  editedValue?: string;
  /** 저장/편집 경계에서 쓰는 canonical cell id */
  editCellId?: string;
  /** metadata 9개 필드 기반 동일 값 그룹 키 */
  cellIdentityKey?: string;
  onEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
  onEditStart?: () => void;
  /** 편집 입력창에 원본 원값 표시 여부 */
  showRawValueInEdit?: boolean;
  /** 선택/포커스 경계에서 쓰는 DOM id */
  selectionId?: string;
  /** 연동 계정 인덱스가 사용하는 원본 보고서 위치 UID */
  identityLocationUid?: string;
  /** block item 선택 마우스 이벤트 핸들러 */
  onCellMouseDown?: (cellId: string, event: React.MouseEvent) => void;
  onCellMouseEnter?: (event: React.MouseEvent) => void;
  onCellMouseUp?: () => void;
}

/**
 * Chunk 내 blockItems를 라벨/값 쌍으로 표시하고 구분선으로 구분
 */
const BlockItemViewComponent = ({
  title,
  showTitle = true,
  content,
  sectionId,
  schemaType,
  unitText,
  blockItemId,
  editable,
  editedValue,
  editCellId,
  cellIdentityKey,
  onEdit,
  onEditStart,
  showRawValueInEdit = false,
  selectionId,
  identityLocationUid,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
}: BlockItemViewProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");
  const [editStartValue, setEditStartValue] = useState("");
  const [optimisticValue, setOptimisticValue] = useState<string | undefined>(
    undefined,
  );
  const editorRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const isConfirmingRef = useRef(false);

  useEffect(() => {
    if (optimisticValue !== undefined && editedValue === optimisticValue) {
      setOptimisticValue(undefined);
    }
  }, [editedValue, optimisticValue]);

  const effectiveEditedValue = optimisticValue ?? editedValue;
  const hasBeenEdited = effectiveEditedValue !== undefined;
  const rawText = hasBeenEdited ? effectiveEditedValue : String(content);
  const valueKind = getIxdSchemaValueKind(schemaType);
  const isMoney = valueKind === "MONETARY";
  const rawNum = parseNumericValue(rawText);
  const isEditableBlock = !!editable && !!onEdit && !!editCellId;
  const isSelectableBlock = !!selectionId && !!editCellId;

  const { displayText, unitDivisor } = formatCellForDisplay({
    rawText,
    schemaType,
    unitText,
  });

  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.focus();
      editorRef.current.select();
    }
  }, [isEditing]);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!isEditing || !(editor instanceof HTMLTextAreaElement)) return;
    editor.style.height = "auto";
    editor.style.height = `${editor.scrollHeight}px`;
  }, [isEditing, localValue]);

  const handleStartEdit = () => {
    if (!isEditableBlock) return;
    onEditStart?.();
    // 표시와 같은 문자열 십진 경로 — Number 경유 정수 반올림의 소수 유실 방지
    const initial =
      isMoney && rawNum !== null
        ? ((showRawValueInEdit
            ? formatMonetaryTextForEditing({ rawText, divisor: 1 })
            : formatMonetaryTextForEditing({
                rawText,
                divisor: unitDivisor,
              })) ??
          String(showRawValueInEdit ? rawNum : applyUnit(rawNum, unitDivisor)))
        : formatStoredValueForEditing(schemaType, rawText);
    setLocalValue(initial);
    setEditStartValue(initial);
    setIsEditing(true);
  };

  const finishConfirmEdit = () => {
    setIsEditing(false);
    window.setTimeout(() => {
      isConfirmingRef.current = false;
    }, 0);
  };

  const handleConfirmEdit = () => {
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;

    if (!onEdit || !editCellId) {
      finishConfirmEdit();
      return;
    }

    if (localValue === editStartValue) {
      finishConfirmEdit();
      return;
    }

    const normalized = normalizeEditedCellValue({
      schemaType,
      inputValue: localValue,
      unitDivisor,
      displayValueMode: showRawValueInEdit ? "raw" : "unit-applied",
      displayUsesNegatedSign: false,
      negated: false,
      decimalDigits: null,
    });

    if (!normalized.ok) {
      console.error("[DEBUG] IXD 블록 값 편집 검증 실패", {
        cellId: editCellId,
        schemaType,
        inputValue: localValue,
        currentRawText: rawText,
        unitDivisor,
        displayValueMode: showRawValueInEdit ? "raw" : "unit-applied",
        errorMessage: normalized.errorMessage,
      });
      toast.error(normalized.errorMessage);
      finishConfirmEdit();
      return;
    }

    if (normalized.storedValue === rawText) {
      finishConfirmEdit();
      return;
    }

    setOptimisticValue(normalized.storedValue);
    onEdit(
      editCellId,
      normalized.storedValue,
      cellIdentityKey,
      identityLocationUid,
    );
    finishConfirmEdit();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleConfirmEdit();
    } else if (event.key === "Escape") {
      handleCancelEdit();
    } else {
      skipSeparatorOnDelete(schemaType, event);
    }
  };

  const handleTextareaKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      handleConfirmEdit();
    } else if (event.key === "Escape") {
      handleCancelEdit();
    }
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!isSelectableBlock || isEditing || !selectionId || !onCellMouseDown) {
      return;
    }
    onCellMouseDown(selectionId, event);
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (!isSelectableBlock || isEditing || !onCellMouseEnter) return;
    onCellMouseEnter(event);
  };

  const handleMouseUp = () => {
    if (!isSelectableBlock || isEditing || !onCellMouseUp) return;
    onCellMouseUp();
  };

  const usesMultilineEditor =
    valueKind === "TEXT" ||
    displayText.includes("\n") ||
    rawText.includes("\n");
  const editorSizerText = displayText || localValue || " ";
  const editorClassName =
    "col-start-1 row-start-1 w-full min-w-0 box-border bg-background-base border border-primary-base rounded px-1 py-0 caption-2-medium text-label-normal outline-none";

  const blockContent = isEditing ? (
    <div className="grid min-w-0">
      <span
        aria-hidden
        className="invisible col-start-1 row-start-1 block min-h-[1.375rem] whitespace-pre-wrap break-words"
      >
        {editorSizerText}
      </span>
      {usesMultilineEditor ? (
        <textarea
          ref={editorRef as React.RefObject<HTMLTextAreaElement>}
          value={localValue}
          maxLength={getEditableMaxLengthForSchema(schemaType)}
          onChange={(event) =>
            setLocalValue(
              sanitizeEditableInputForSchema(schemaType, event.target.value),
            )
          }
          onBlur={handleConfirmEdit}
          onKeyDown={handleTextareaKeyDown}
          title="값 편집"
          rows={1}
          className={cn(
            editorClassName,
            "min-h-[1.375rem] resize-none overflow-hidden whitespace-pre-wrap break-words",
          )}
        />
      ) : (
        <input
          ref={editorRef as React.RefObject<HTMLInputElement>}
          type="text"
          value={localValue}
          inputMode={getEditableInputModeForSchema(schemaType)}
          maxLength={getEditableMaxLengthForSchema(schemaType)}
          onChange={(event) =>
            setLocalValue(
              sanitizeEditableInputElement(schemaType, event.target),
            )
          }
          onBlur={handleConfirmEdit}
          onKeyDown={handleInputKeyDown}
          title="값 편집"
          className={editorClassName}
        />
      )}
    </div>
  ) : (
    <span className="whitespace-pre-wrap break-words">{displayText}</span>
  );

  const valueDataset = buildValueCellDataset({
    cellId: editCellId,
    sourceCellId: blockItemId ?? undefined,
    rawText,
    displayText,
    identityKey: cellIdentityKey,
    schemaType,
    unitDivisor,
    negated: false,
    decimalDigits: null,
    readonly: !isEditableBlock,
    edited: hasBeenEdited,
  });

  return (
    <div
      className="flex flex-col gap-[0.75rem]"
      {...(sectionId ? { id: sectionId } : {})}
    >
      {showTitle && (
        <div className="label-2-medium text-label-normal">{title}</div>
      )}
      <IxdTable.NarrativeBlock
        id={selectionId || blockItemId || editCellId || undefined}
        data-cell-identity-location-uid={identityLocationUid}
        className={cn(
          "box-border border border-line-normal",
          hasBeenEdited && !isEditing && "bg-amber-50",
          isEditing && "bg-amber-50 ring-1 ring-inset ring-amber-300",
          isSelectableBlock &&
            !isEditing &&
            (hasBeenEdited
              ? "cursor-pointer hover:bg-amber-100 select-none"
              : "cursor-pointer hover:bg-slate-50 select-none"),
        )}
        onDoubleClick={
          isEditableBlock && !isEditing ? handleStartEdit : undefined
        }
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseUp={handleMouseUp}
        {...valueDataset}
      >
        {blockContent}
      </IxdTable.NarrativeBlock>
    </div>
  );
};

export const BlockItemView = memo(BlockItemViewComponent);

BlockItemView.displayName = "BlockItemView";
